import './CallDetailsModal.css';
import ReactMarkdown from 'react-markdown';
import React, { useContext, useState } from 'react';
import { Alert, Button, Descriptions, Modal, Typography, message } from 'antd';
import dayjs from 'dayjs';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import getVoipCallRecording from '../../../../domain/use_cases/leedflow/voipCalls/getVoipCallRecording';
import retryCallTranscription, { canRetryCallSummary } from '../../../../domain/use_cases/leedflow/voipCalls/retryCallTranscription';
import { formatCallNumber, renderDevice, renderDirection, renderPlatform } from './callCells';
import type { LastCallEntry } from '../../../../domain/use_cases/leedflow/overview/getOverviewStats';

interface CallDetailsModalProps {
  call: LastCallEntry | null;
  onClose: () => void;
}

const formatDuration = (seconds: number | null): string => {
  if (!seconds || seconds <= 0) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}min ${s}s` : `${s}s`;
};

// Reprend `normalizeTranscription` de LastCallsTable : certains appels stockent
// la synthèse IA sous forme d'objet structuré et non de chaîne.
const normalizeTranscription = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, v]) => `${key}\n${typeof v === 'string' ? v : JSON.stringify(v)}`)
      .join('\n\n');
  }
  return '';
};

const stateLabel = (call: LastCallEntry): string => {
  if (call.missed === null) return 'État inconnu';
  if (call.missed) return 'Manqué';
  return (call.duration ?? 0) > 0 ? 'Décroché' : 'Sans réponse';
};

// La relance peut juger l'appel non enregistré : le backend a alors masqué le fichier.
const RecordingBlock: React.FC<{ hidden: boolean; url: string | null; loading: boolean; onListen: () => void }> = ({
  hidden,
  url,
  loading,
  onListen,
}) => {
  if (hidden) {
    return <Typography.Text type="secondary">Enregistrement masqué : appel jugé non enregistré.</Typography.Text>;
  }
  if (url) {
    // Un enregistrement d'appel n'a pas de piste de sous-titres à fournir.
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <audio controls autoPlay src={url} style={{ width: '100%' }} />;
  }
  return (
    <Button loading={loading} onClick={onListen}>
      Écouter
    </Button>
  );
};

const SummaryBlock: React.FC<{ transcription: string; canRetry: boolean; retrying: boolean; onRetry: () => void }> = ({
  transcription,
  canRetry,
  retrying,
  onRetry,
}) => {
  if (transcription) {
    return (
      <div className="call-summary">
        <ReactMarkdown>{transcription}</ReactMarkdown>
      </div>
    );
  }
  return (
    <>
      <Typography.Text type="secondary">Aucun résumé sur cet appel.</Typography.Text>
      {canRetry && (
        <Button loading={retrying} onClick={onRetry} style={{ marginLeft: 12 }}>
          {retrying ? 'Nouvelle analyse en cours...' : 'Relancer le résumé'}
        </Button>
      )}
    </>
  );
};

const CallDetailsModal: React.FC<CallDetailsModalProps> = ({ call, onClose }) => {
  const authContext = useContext(AuthenticationContext);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Rattachés à l'appel relancé : la modale reste montée d'un appel à l'autre,
  // et une relance encore en vol ne doit jamais s'afficher sur un autre appel.
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retried, setRetried] = useState<{ id: string; summary: string | null; recorded: boolean } | null>(null);

  // L'enregistrement n'est jamais dans la charge utile de la page : il est
  // streamé à la demande par la même route que la page VoIP, qui renvoie un
  // blob local plutôt qu'une URL signée expirable.
  const loadRecording = async (docId: string) => {
    setLoading(true);
    try {
      const url = await getVoipCallRecording(authContext.authentication, docId);
      if (url) setRecordingUrl(url);
      // `null` = session ou identifiant manquant : rien n'a été demandé au
      // backend, donc aucun message à relayer. Le bouton ne doit pas pour
      // autant rester sans réaction.
      else message.error("Impossible de charger l'enregistrement.");
    } catch (e) {
      // Le backend dit s'il n'y a pas d'enregistrement sur cet appel ou si le
      // fichier n'a pas pu être servi : les deux se règlent différemment.
      message.error(e instanceof Error ? e.message : "Impossible de charger l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  // Le backend refait la transcription si besoin, le résumé, l'historique du
  // contact et la détection de tâches. Il refuse seul les cas non relançables.
  const retrySummary = async (docId: string) => {
    setRetryingId(docId);
    try {
      const result = await retryCallTranscription(authContext.authentication, docId);
      setRetried({ id: docId, ...result });
      if (result.recorded) message.success('Résumé généré');
      else message.warning("Appel jugé non enregistré : l'enregistrement est masqué.");
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Impossible de relancer le résumé de cet appel.');
    } finally {
      setRetryingId((current) => (current === docId ? null : current));
    }
  };

  const handleClose = () => {
    if (recordingUrl) URL.revokeObjectURL(recordingUrl);
    setRecordingUrl(null);
    onClose();
  };

  if (!call) return null;

  const retriedHere = retried?.id === call.id ? retried : null;
  const transcription = retriedHere?.summary ?? normalizeTranscription(call.transcription);

  return (
    <Modal open onCancel={handleClose} footer={null} width={720} title="Détail de l'appel">
      {call.confidential && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Cet appel est passé avec un contact confidentiel : aucun résumé ni analyse IA n'est produit dessus"
        />
      )}

      <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Date">
          {call.created ? dayjs(call.created).format('DD/MM/YYYY HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Sens">{renderDirection(call.direction)}</Descriptions.Item>
        <Descriptions.Item label="État">{stateLabel(call)}</Descriptions.Item>
        <Descriptions.Item label="Durée">{formatDuration(call.duration)}</Descriptions.Item>
        <Descriptions.Item label="Contact">{formatCallNumber(call.customer)}</Descriptions.Item>
        <Descriptions.Item label="Ligne Leedflow">{formatCallNumber(call.sender)}</Descriptions.Item>
        <Descriptions.Item label="Plateforme">{renderPlatform(call.platform)}</Descriptions.Item>
        <Descriptions.Item label="Appareil">{renderDevice(call.device)}</Descriptions.Item>
        <Descriptions.Item label="Identifiant" span={2}>
          <Typography.Text copyable code>{call.id}</Typography.Text>
        </Descriptions.Item>
      </Descriptions>

      {/* L'enregistrement reste écoutable sur un contact confidentiel, décision
          produit assumée. Seul le résumé tombe, et il n'est de toute façon
          jamais produit sur ces appels (cf. `bConfidential`, callrHelpers). */}
      <Typography.Title level={5}>Enregistrement</Typography.Title>
      <RecordingBlock
        hidden={retriedHere?.recorded === false}
        url={recordingUrl}
        loading={loading}
        onListen={() => loadRecording(call.id)}
      />

      <Typography.Title level={5} style={{ marginTop: 16 }}>
        Résumé
      </Typography.Title>
      <SummaryBlock
        transcription={transcription}
        canRetry={canRetryCallSummary(call)}
        retrying={retryingId === call.id}
        onRetry={() => retrySummary(call.id)}
      />
    </Modal>
  );
};

export default CallDetailsModal;
